import { BehaviorSubject, Observable, filter, map, merge, share, switchMap, tap } from 'rxjs';
import { Pub, State, Sub } from '../../../../../../shared/state/state.abstract';
import {
  OverviewArticlesRes,
  overviewService,
  SavePositionsInMultipleOrdersMutationRes,
} from '../../../../services/overview.service';
import { overviewState } from '../../../states/overview.state';
import { responseHandler } from '../../../../../../shared/responseHandler/responseHandler';
import { dataHelper } from '../../../../../../shared/helpers/data/data.helper';
import { storageHelper } from '../../../../../../shared/helpers/storage';
import { GridSortModel } from '@mui/x-data-grid-premium';
import { snackbarService } from '../../../../../../shared/components/snackbar/service/snackbar.service.ts';
import { t } from 'i18next';
import { MutableRefObject } from 'react';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { Wa_SavePositionsInMultiOrdersInputs } from '../../../../../../graphql/generatedModel.ts';
import { Stack, Typography } from '@mui/material';
import { navBlocker } from '../../../../../../shared/components/navigation/blocker/nav.blocker.ts';

export const initArticlesOverviewState: IArticlesOverviewState = {
  action: undefined,
  articleOverview: [],
  tableApiRef: undefined,
  isActiveGroupMode: true,
  search: '',
  sortModel: [{ field: 'orderNo', sort: 'asc' }],
};
class PubImpl extends Pub<IArticlesOverviewState> {
  init(params: Partial<IArticlesOverviewState>) {
    this.emit('init', params);
  }
  getTableApiRef(tableApiRef: IArticlesOverviewState['tableApiRef']) {
    this.emit('getTableApiRef', { tableApiRef });
  }
  toggleGroupMode() {
    this.emit('toggleGroupMode', {});
  }
  search(search: string) {
    this.emit('search', { search });
  }
  saveData() {
    this.emit('saveData', {});
  }
  deleteArticleFromAllOrders(articleToDeleteRows: OverviewArticleData) {
    this.emit('deleteArticleFromAllOrders', { articleToDeleteRows });
  }
  updateSaveStatus() {
    this.emit('updateSaveStatus', {});
  }
  sort(sortModel: IArticlesOverviewState['sortModel']) {
    storageHelper.local.setItem('overview.articleSort', sortModel);
    this.emit('sort', { sortModel });
  }
}
class SubImpl extends Sub<IArticlesOverviewState> {
  private loading$ = new BehaviorSubject<boolean>(false);
  private shareLoading$: Observable<boolean> = this.loading$.pipe(share());
  private isSaveButton$ = new BehaviorSubject<boolean>(false);
  private shareIsSaveButton$: Observable<boolean> = this.isSaveButton$.pipe(share());
  private saveLoading$ = new BehaviorSubject<boolean>(false);
  private shareSaveLoading$: Observable<boolean> = this.saveLoading$.pipe(share());

  protected actionHandlers(): Observable<IArticlesOverviewState> {
    return merge(
      this.getData(),
      this.toggleGroupMode(),
      this.updateArticlesOverview(),
      this.updateSaveStatus(),
      this.saveData(),
      this.deleteArticleFromAllOrders(),
    ).pipe(
      map((state) => {
        const { search, articleOverview, sortModel } = state;
        let updatedArticlesOverview = [...(articleOverview || [])] as OverviewArticleData;

        updatedArticlesOverview = dataHelper
          .data(articleOverview as [])
          .sort({ sortModel })
          .search({
            search,
            fields: [
              'article',
              'customerNo',
              'internalOrFullName',
              'indTextDeliveryNote',
              'indTextProduction',
              'orderNo',
              ['orderDate'],
            ],
          })
          .result() as OverviewArticleData;

        this.stream$.next({ ...state, action: 'internalUpdate' });
        return { ...state, articleOverview: updatedArticlesOverview };
      }),
    );
  }

  loading(): Observable<boolean> {
    return this.shareLoading$;
  }

  showSaveButton(): Observable<boolean> {
    return this.shareIsSaveButton$.pipe(
      tap((dirty) => {
        navBlocker.watch(dirty);
      }),
    );
  }

  saveLoading(): Observable<boolean> {
    return this.shareSaveLoading$;
  }

  private updateArticlesOverview(): Observable<IArticlesOverviewState> {
    return this.actionListener(['search', 'sort']);
  }

  private getData(): Observable<IArticlesOverviewState> {
    return overviewState.sub.state().pipe(
      filter(
        ({ action }) => action === 'updateDatePeriod' || action === 'updateDeliverynoteGroupId',
      ),
      tap(() => {
        this.loading$.next(true);
        this.isSaveButton$.next(false);
      }),
      switchMap(({ datePeriod, deliveryNoteGroupId }) => {
        overviewService.pub.getOverviewArticles({
          filter: {
            periodStart: datePeriod?.fromDate,
            periodEnd: datePeriod?.toDate,
            deliveryNoteGroupId,
          },
        });
        return overviewService.sub.getOverviewArticles().pipe(
          responseHandler<OverviewArticlesRes>({
            customErrorHandler: () => 'order.tab_not_loaded',
            errorReturnType: [],
          }),
          map((data) => {
            const state = this.stream$.getValue();
            this.loading$.next(false);
            return {
              ...state,
              action: 'getData',
              articleOverview: concatArticleNameAndNo(data),
            } as IArticlesOverviewState;
          }),
        );
      }),
    );
  }

  private toggleGroupMode(): Observable<IArticlesOverviewState> {
    return this.actionListener('toggleGroupMode').pipe(
      map((oldState) => {
        const state = structuredClone(oldState);
        const oldStatus = state?.isActiveGroupMode;
        const newStatus = !oldStatus;
        storageHelper.local.setItem('overview.articleIsGroupModeActive', newStatus);
        state.isActiveGroupMode = newStatus;
        return state;
      }),
    );
  }

  private saveData(): Observable<IArticlesOverviewState> {
    return this.actionListener('saveData').pipe(
      map((state) => {
        const { tableApiRef } = state;
        const sortedRows = tableApiRef?.current?.getSortedRows();
        const changedData =
          sortedRows?.filter(
            (el) => el.manualChanged && el.quantity !== null && el.quantity !== undefined,
          ) || [];
        return { ...state, changedData };
      }),
      filter(({ changedData }) => Boolean(changedData.length)),
      tap(({ changedData }) => {
        const params = this.prepareDataForSave(changedData as OverviewArticleData);
        overviewService.pub.savePositionsInMultipleOrders({ params });
        snackbarService.pub.hide();
        snackbarService.pub.show({
          id: 'articleSaveOverviewLoading',
          type: 'loading',
          content: t('common.saving_in_progress'),
          noAutoHide: true,
        });
        this.saveLoading$.next(true);
      }),
      switchMap((state) => {
        return overviewService.sub.savePositionsInMultipleOrders().pipe(
          responseHandler<SavePositionsInMultipleOrdersMutationRes>({
            errorReturnType: state.changedData.map((el) => ({
              id: String(el.orderId),
              success: false,
              errorMsg: null,
              errorCode: null,
            })),
            quite: true,
          }),
          map((response) => {
            const { changedData, tableApiRef } = state;
            const idsResponse = response.reduce<{
              errorOrderIds: Array<string>;
              successOrderIds: Array<string>;
            }>(
              (acc, res) => {
                if (res.success) {
                  acc.successOrderIds.push(res.id);
                } else acc.errorOrderIds.push(res.id);
                return acc;
              },
              { errorOrderIds: [], successOrderIds: [] },
            );
            const updatedChangedData = changedData.map((row) => {
              if (idsResponse.successOrderIds.includes(String(row.orderId))) {
                return {
                  ...row,
                  manualChanged: false,
                  primaryQuantity: row.quantity,
                  error: false,
                };
              } else if (idsResponse.errorOrderIds.includes(String(row.orderId))) {
                return { ...row, error: true };
              } else return row;
            });
            tableApiRef?.current?.updateRows(updatedChangedData);
            snackbarService.pub.hide('articleSaveOverviewLoading');
            this.showSaveMessage(idsResponse);
            this.saveLoading$.next(false);
            return state;
          }),
        );
      }),
      filter(() => false),
    );
  }

  private deleteArticleFromAllOrders(): Observable<IArticlesOverviewState> {
    return this.actionListener('deleteArticleFromAllOrders').pipe(
      filter(({ articleToDeleteRows }) => Boolean(articleToDeleteRows?.length)),
      tap(({ articleToDeleteRows }) => {
        const rowsToDelete = articleToDeleteRows as OverviewArticleData;
        const params = this.prepareDataForSave(
          rowsToDelete.map((row) => ({ ...row, quantity: 0 })),
        );
        overviewService.pub.deleteArticleFromAllOrders({ params });
        snackbarService.pub.hide();
        snackbarService.pub.show({
          id: 'articleDeleteOverviewLoading',
          type: 'loading',
          content: t('common.saving_in_progress'),
          noAutoHide: true,
        });
        this.saveLoading$.next(true);
      }),
      switchMap((state) => {
        const rowsToDelete = (state.articleToDeleteRows || []) as OverviewArticleData;
        const uniqueOrderIds = Array.from(new Set(rowsToDelete.map((row) => String(row.orderId))));
        return overviewService.sub.deleteArticleFromAllOrders().pipe(
          responseHandler<SavePositionsInMultipleOrdersMutationRes>({
            errorReturnType: uniqueOrderIds.map((id) => ({
              id,
              success: false,
              errorMsg: null,
              errorCode: null,
            })),
            quite: true,
          }),
          map((response) => {
            const idsResponse = response.reduce<{
              errorOrderIds: Array<string>;
              successOrderIds: Array<string>;
            }>(
              (acc, res) => {
                if (res.success) {
                  acc.successOrderIds.push(res.id);
                } else acc.errorOrderIds.push(res.id);
                return acc;
              },
              { errorOrderIds: [], successOrderIds: [] },
            );
            const successOrderIds = new Set(idsResponse.successOrderIds.map(String));
            const removedRowIds = new Set(
              rowsToDelete
                .filter((row) => successOrderIds.has(String(row.orderId)))
                .map((row) => row.id),
            );
            const failedRowIds = new Set(
              rowsToDelete
                .filter((row) => !successOrderIds.has(String(row.orderId)))
                .map((row) => row.id),
            );
            const currentState = this.stream$.getValue();
            const reducedArticleOverview = (currentState.articleOverview || [])
              .filter((row) => !removedRowIds.has(row.id))
              .map((row) =>
                failedRowIds.has(row.id) ? { ...row, error: true } : row,
              ) as OverviewArticleData;
            snackbarService.pub.hide('articleDeleteOverviewLoading');
            this.showSaveMessage(idsResponse);
            const hasPendingChanges =
              currentState.tableApiRef?.current
                ?.getSortedRows()
                ?.some((row) => row.manualChanged && !removedRowIds.has(row.id)) ?? false;
            if (hasPendingChanges) {
              this.isSaveButton$.next(true);
            }
            this.saveLoading$.next(false);
            return {
              ...currentState,
              action: 'getData',
              articleToDeleteRows: undefined,
              articleOverview: reducedArticleOverview,
            } as IArticlesOverviewState;
          }),
        );
      }),
    );
  }

  private updateSaveStatus(): Observable<IArticlesOverviewState> {
    return this.actionListener('updateSaveStatus').pipe(
      filter(({ tableApiRef }) => !!tableApiRef),
      tap(({ tableApiRef }) => {
        const rows = tableApiRef?.current.getSortedRows() || [];
        const isDirty = rows.some((row) => row?.manualChanged);
        this.isSaveButton$.next(isDirty);
      }),
      filter(() => false),
    );
  }

  private prepareDataForSave(data: OverviewArticleData) {
    return data.reduce<Wa_SavePositionsInMultiOrdersInputs[]>((acc, item) => {
      const foundIndex = acc.findIndex((el) => el.id === String(item.orderId));
      if (foundIndex !== -1) {
        const orderData = acc[foundIndex];
        acc[foundIndex] = {
          ...orderData,
          positions: [
            ...(orderData.positions || []),
            {
              quantity: item.quantity!,
              positionId: item.positionId,
            },
          ],
        };
      } else {
        acc.push({
          id: String(item.orderId),
          modificationOptions: {
            ignoreDaysToProduce: true,
            ignoreMinMaxProblems: true,
            ignoreSplittingProblems: true,
            ignoreQuantityPerLotProblems: true,
          },
          positions: [
            {
              quantity: item.quantity!,
              positionId: item.positionId,
            },
          ],
        });
      }
      return acc;
    }, []);
  }

  private showSaveMessage(responseIds: {
    errorOrderIds: Array<string>;
    successOrderIds: Array<string>;
  }): void {
    switch (true) {
      case !!responseIds.successOrderIds.length && !responseIds.errorOrderIds.length:
        this.isSaveButton$.next(false);
        snackbarService.pub.show({
          type: 'success',
          content: t('common.saved_successfully'),
        });
        break;
      case !responseIds.successOrderIds.length && !!responseIds.errorOrderIds.length:
        snackbarService.pub.show({
          type: 'error',
          content: t('common.error_chnages_not_saved'),
        });
        break;
      default:
        snackbarService.pub.show({
          type: 'warning',
          maxWidth: 350,
          content: (
            <Stack>
              <Typography variant='body2'>{t('order.changes_saved')}</Typography>
              <Typography
                component='span'
                variant='body2'
                sx={{
                  pl: 1,
                }}
              >
                {t('order.success_count', { count: responseIds.successOrderIds.length })}
              </Typography>
              <Typography
                component='span'
                variant='body2'
                sx={{
                  pl: 1,
                }}
              >
                {t('order.error_count', { count: responseIds.errorOrderIds.length })}
              </Typography>
            </Stack>
          ),
        });
    }
  }
}

class ArticleOverviewState extends State<IArticlesOverviewState> {
  pub = new PubImpl(this.stream$);
  sub = new SubImpl(this.stream$);

  getArticleOverviewSnapshot(): OverviewArticleData {
    return this.stream$.getValue().articleOverview || [];
  }
}
export const articleOverviewState = new ArticleOverviewState(initArticlesOverviewState);

export const concatArticleNameAndNo = (data: OverviewArticlesRes): OverviewArticleData => {
  if (!data) {
    return [];
  }
  return data.map((props, i) => {
    const { articleNo, articleName } = props || {};
    return {
      ...props,
      article: `${articleNo} ${articleName}`,
      id: String(i),
    };
  }) as OverviewArticleData;
};

export const getInitSortModel = (): GridSortModel => {
  const initSortModel =
    storageHelper.local.getItem('overview.articleSort') || initArticlesOverviewState?.sortModel;
  return initSortModel;
};

type ArticleItem = NonNullable<NonNullable<OverviewArticlesRes>[number]>;
interface ArticleData extends ArticleItem {
  article: string;
  id: string;
}
export type OverviewArticleData = ArticleData[];
export interface IArticlesOverviewState {
  action:
    | 'init'
    | 'internalUpdate'
    | 'getData'
    | 'toggleGroupMode'
    | 'search'
    | 'sort'
    | 'updateChangedData'
    | 'saveData'
    | 'getTableApiRef'
    | 'updateSaveStatus'
    | 'deleteArticleFromAllOrders'
    | undefined;
  articleOverview: OverviewArticleData;
  tableApiRef?: MutableRefObject<GridApiPremium> | undefined;
  isActiveGroupMode: boolean;
  search: string;
  sortModel: GridSortModel;
  articleToDeleteRows?: OverviewArticleData;
}
